import { AI_API_ROOT, AI_API_HEADERS, AI_CURRENT_USER, AI_CURRENT_USER_AVATAR, AI_MESSAGES_INTERCEPTOR } from '@aston/ai-chat';
import { AppErrorHandler, DEBUG_BAR_CONFIG, DebugBarConfig, ScrolledByService, sharePrefix, UserSessionService } from '@aston/foundation';
import { IsActiveMatchOptions, Router } from '@angular/router';
import { ErrorHandler, inject } from '@angular/core';
import { filter, map, throttleTime } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { FederationAIChatBoxMessagesInterceptor } from './shared-module/services/ai-chatbox.interceptor';
import { FEDERATION_API_ROOT, WebSocketService } from './shared-module/services';
import { AuthenticationService } from './authentication-module/services';
import { AppStoreSelectors } from './root-store/app-store';
import { AppConfiguration } from './app.configuration';
import { AppConstants } from './app.constants';
import { AuthenticationInterceptor } from './authentication-module/interceptors';

export const apiRoot = (store: Store) => store.select(AppStoreSelectors.selectConfig).pipe(map(c => c.apiUrl))

export function onViewTransitionCreated({transition}) {
	const router = inject(Router);
	const currentUrl = router.lastSuccessfulNavigation()?.finalUrl;
	const targetUrl = router.currentNavigation()?.finalUrl;
	if (!currentUrl || !targetUrl) return false;

	// Skip the transition if the only thing
	// changing is the fragment and queryParams
	const config: IsActiveMatchOptions = {
		paths: 'subset',
		matrixParams: 'ignored',
		fragment: 'ignored',
		queryParams: 'ignored',
	};
	if (router.isActive(targetUrl, config)
		// Also skip between two same /word/:id/deep/pages
		|| sharePrefix(router.serializeUrl(currentUrl), router.serializeUrl(targetUrl))
	) {
		transition.skipTransition();
	}
}

export const ctxHelpConnector = (store: Store) => ({
	cxtHelpBaseUrl: store.select(AppStoreSelectors.selectConfig).pipe(map(c => c?.knowledgeBaseUrl))
})

export function provideDebugBar() {
	return [{
		provide: DEBUG_BAR_CONFIG,
		useFactory: (
			errorHandler: ErrorHandler,
			configuration: AppConfiguration,
			scrollService: ScrolledByService,
			store: Store,
			auth: AuthenticationService,
			websocket: WebSocketService,
			sessions: UserSessionService,
		): DebugBarConfig => ({
			configuration,
			scrollService,
			errors: errorHandler as AppErrorHandler,
			tenant$: store.select(AppStoreSelectors.selectTenant).pipe(filter(Boolean),map(t => ({ ...t, id: t.tenantId }))),
			sessionId$: sessions.sessionID$,
			inactivityService: {
				inactivityCountdown$: store.select(AppStoreSelectors.selectInactivityCountDown).pipe(throttleTime(1000)),
				inGracefulPeriod$: store.select(AppStoreSelectors.selectInactivityTime).pipe(map(inactivity => inactivity >= AppConstants.MAX_INACTIVITY_TIME - AppConstants.INACTIVITY_WARNING_COUNTDOWN_BEFORE_LOGOUT)),
			},
			websocket,
			auth,
		}),
		deps: [
			ErrorHandler,
			AppConfiguration,
			ScrolledByService,
			Store,
			AuthenticationService,
			WebSocketService,
			UserSessionService,
		]
	}]
}

export function provideAIChat() {
	return [
		{ provide: AI_CURRENT_USER_AVATAR, useFactory: (store: Store) => store.select(AppStoreSelectors.selectCurrentUserAvatar), deps: [Store] },
		{ provide: AI_CURRENT_USER, useFactory: (store: Store) => store.select(AppStoreSelectors.selectCurrentUser), deps: [Store] },
		{ provide: AI_API_ROOT, useFactory: (FEDERATION_API_ROOT) => FEDERATION_API_ROOT, deps: [FEDERATION_API_ROOT] },
		{ provide: AI_API_HEADERS, useFactory: (int: AuthenticationInterceptor) => int.getAuthHeaders(), deps: [AuthenticationInterceptor] },
		{ provide: AI_MESSAGES_INTERCEPTOR, useClass: FederationAIChatBoxMessagesInterceptor },
	]
}
